import React, {useEffect, useState} from 'react';
import Table from '../../common/table/Table';
import {Column} from "../../common/table/TableSection";
import {useNavigate} from "react-router-dom";
import styles from "./AdminWorkerRequestTable.module.css";
import {GetRegularTaxInvoiceTableResponseData} from "../../../types/admin";
import {InvoiceTarget, RegularInvoiceSort} from "../../../types/contract";
import {adminRegularTaxInvoiceTableAPI} from "../../../services/admin";
import Button from "../../common/button/Button";
import CreateRegularTaxInvoicePopUp from "../../../views/admin/popup/CreateRegularTaxInvoicePopUp";

//어드민 정기 세금 계산서 발행 테이블
const AdminRegularTaxInvoiceTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<GetRegularTaxInvoiceTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [filters, setFilters] = useState<Record<string, any>>({
        min: '',
        max: ''
    });

    // 필터 전체 초기화
    const resetAllFilters = () => {
        // 필터 초기화
        setFilters({
            min: '',
            max: ''
        });
        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });
    };
    //테이블 로우 선택 시
    const handleRowClick = (row: GetRegularTaxInvoiceTableResponseData) => {
        navigate(`/admin/billing/regular/${row.regularTaxInvoiceId}`);
    };

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    //컬럼
    const columns: Column<GetRegularTaxInvoiceTableResponseData>[] = [
        {
            key: 'No',
            header: '번호',
            width: '8%'
        },
        {
            key: 'companyName',
            header: '회사명',
            width: '35%',
            sortable: true
        },
        {
            key: 'day',
            header: '정기발행일',
            width: '22%',
            sortable: false,
            render: value => "매월" + value + "일"
        },
        {
            key: 'amount',
            header: '총액',
            width: '35%',
            sortable: true,
            render: value => value.toLocaleString() + "원"
        }
    ];

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'amount',
            label: '총액',
            component: (
                <div className={styles.numberRangeFilter}>
                    <span>총액: </span>
                    <div className={styles.rangeInput}>
                        <input
                            type="number"
                            min="0"
                            placeholder="최소"
                            value={filters.min}
                            onChange={(e) => handleFilterChange('min', e.target.value)}
                            className={styles.numberInput}
                            title="최소 총액"
                        />
                        <span className={styles.rangeSeparator}>~</span>
                        <input
                            type="number"
                            min="0"
                            placeholder="최대"
                            value={filters.max}
                            onChange={(e) => handleFilterChange('max', e.target.value)}
                            className={styles.numberInput}
                            title="최대 총액"
                        />
                    </div>
                </div>
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): RegularInvoiceSort => {
        if (!field || !direction) return RegularInvoiceSort.COMPANY_ASC;
        const sortMap: Record<string, Record<string, RegularInvoiceSort>> = {
            companyName: {
                asc: RegularInvoiceSort.COMPANY_ASC,
                desc: RegularInvoiceSort.COMPANY_DESC
            },
            amount: {
                asc: RegularInvoiceSort.AMOUNT_ASC,
                desc: RegularInvoiceSort.AMOUNT_DESC
            }
        };
        return sortMap[field]?.[direction] || RegularInvoiceSort.COMPANY_ASC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //API 추가
            const response = await adminRegularTaxInvoiceTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                target: searchTerm,
                search: searchTerm,
                min: filters.min,
                max: filters.max
            });

            setData(response.content);
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: InvoiceTarget.COMPANY_NAME, label: '회사명'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            if (key === 'min' || key === 'max') {
                if (value === '' || /^\d+$/.test(value)) {
                    const numberValue = value === '' ? '' : parseInt(value);
                    if (key === 'min' && prev.max !== '' && numberValue > parseInt(prev.max)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    if (key === 'max' && prev.min !== '' && numberValue !== '' && numberValue < parseInt(prev.min)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    return {...prev, [key]: numberValue};
                }
                return prev;
            }

            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>세금계산서 정기발행</h3>
                    <Button
                        variant="primary"
                        onClick={() => setIsCreatePopupOpen(true)}
                        size={"small"}
                        className={styles.mobileButton}
                    >
                        추가
                    </Button>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        <select
                            value={"회사명"}
                            className={styles.searchTypeSelect}
                            title="검색 유형 선택"
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                            title="검색어 입력"
                        />
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                        <Button
                            onClick={() => setIsCreatePopupOpen(true)}
                            className={styles.pcButton}
                            size={"small"}
                            variant="primary"
                        >
                            추가
                        </Button>
                    </div>
                </div>
            </div>
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
            <CreateRegularTaxInvoicePopUp
                isOpen={isCreatePopupOpen}
                onClose={() => {
                    setIsCreatePopupOpen(false);
                    fetchData();
                }}
            />
        </div>
    );
};

export default AdminRegularTaxInvoiceTable;